import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretSortIcon,
  ExclamationTriangleIcon,
  LayoutIcon,
} from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Popover,
  ScrollArea,
  SegmentedControl,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  Table as ReactTable,
  OnChangeFn,
  RowSelectionState,
  getSortedRowModel,
  ColumnSizingState,
  Header,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { forwardRef, useMemo, useRef, useState } from "react";
import { TableVirtuoso } from "react-virtuoso";
import { twMerge } from "tailwind-merge";

type DataTableProps = {
  title: string;
  data: any[];
  columns: any[];
  totalCount?: number;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  loading?: boolean;
  isSuccess?: boolean;
  onBottomReach?: () => void;
  sorting?: any[];
  onSortingChange?: (value: any) => void;
  renderFilters?: React.ReactNode;
  renderHeader?: React.ReactNode;
  renderPagination?: React.ReactNode;
  rowClassName?: (row: any) => string;
  canToggleView?: boolean;
  visibleColumnNames?: Record<string, string>;
  emptyTitle: string;
  emptyDescription: string;
};

type View = "table" | "grid";

export function DataTable({
  title,
  data,
  columns,
  totalCount = 0,
  loading,
  isSuccess,
  rowSelection,
  sorting,
  renderFilters,
  renderHeader,
  renderPagination,
  canToggleView,
  visibleColumnNames,
  emptyTitle,
  emptyDescription,
  rowClassName,
  onSortingChange,
  onRowSelectionChange,
}: DataTableProps) {
  const [columnVisibility, setColumnVisibility] = useState({});
  const [view, setView] = useState<View>("table");
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    JSON.parse(localStorage.getItem(title) || "{}")
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
      rowSelection,
      sorting,
      columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange,
    enableMultiSort: true,
    manualSorting: true,
    debugTable: false,
    defaultColumn: {
      size: 200, //starting column size
    },
  });

  const { rows } = table.getRowModel();
  const [scrollParent, setScrollParent] = useState<HTMLDivElement>();

  const updateColumnSize = (headerId: string, newWidth: number) => {
    setColumnSizing((prevState) => {
      const newState = {
        ...prevState,
        [headerId]: newWidth,
      };
      localStorage.setItem(title, JSON.stringify(newState));
      return newState;
    });
  };

  const pageRange = useMemo(() => {
    const DATE_FORMAT = "YYYY-MM-DD, HH:mm";
    const startRow = rows.at(0);
    const endRow = rows.at(-1);

    if (
      !startRow ||
      !endRow ||
      !startRow.original.order?.created_at ||
      !endRow.original.order?.created_at
    ) {
      return null;
    }

    const startDate = dayjs(startRow.original.order.created_at).tz("UTC");
    const endDate = dayjs(endRow.original.order.created_at).tz("UTC");
    const startText = startDate.format(DATE_FORMAT);
    const endText = endDate.format(DATE_FORMAT);

    if (startDate.isSame(endDate)) {
      return startText;
    }

    if (startDate.isAfter(endDate)) {
      return endText + " - " + startText;
    }

    return startText + " - " + endText;
  }, [rows]);

  return (
    <div className="border border-gray-200 rounded-lg flex-grow pb-1">
      {renderHeader ? (
        renderHeader
      ) : (
        <Flex justify="between" align="center" p="2">
          <Flex align="center" gap="2">
            <Text size="4" weight="bold">
              {title}
            </Text>
            <Text
              size="2"
              color="gray"
              weight="medium"
              className="tabular-nums"
            >
              ({totalCount} items) {pageRange && `Page range: ${pageRange}`}
            </Text>
          </Flex>
          <Flex align="center">
            {renderPagination}
            {!!visibleColumnNames && (
              <VisibleColumns
                table={table}
                visibleColumnNames={visibleColumnNames}
              />
            )}
            {canToggleView && (
              <SegmentedControl.Root
                ml="2"
                value={view}
                onValueChange={(value) => setView(value as View)}
              >
                <SegmentedControl.Item value="grid">
                  Grid View
                </SegmentedControl.Item>
                <SegmentedControl.Item value="table">
                  Table View
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            )}
          </Flex>
        </Flex>
      )}
      {renderFilters}

      <ScrollArea
        className="relative w-full border-t border-[var(--gray-7)] max-h-[calc(100dvh-400px)] disable-overscroll"
        ref={(el) => setScrollParent(el ?? undefined)}
      >
        {view === "grid" ? (
          <div className="w-full bg-neutral-100 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 p-2">
            {data?.map((item) => (
              <div className="w-full flex gap-2 border border-gray-200 rounded p-2 bg-white">
                <figure className="min-w-20">
                  <img src={item.image} className="w-20 h-20" />
                </figure>
                <div>
                  <Text
                    as="p"
                    size="2"
                    className="leading-tight"
                    mb="2"
                    weight="medium"
                  >
                    {item.display_name}
                  </Text>
                  <Text as="p" color="gray" size="1">
                    SKU ID: {item.id}
                  </Text>
                  <Text as="p" color="gray" size="1">
                    Barcode: {item.sku}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <TableVirtuoso
            useWindowScroll
            customScrollParent={scrollParent}
            totalCount={loading ? 20 : rows.length}
            components={{
              Table: ({ style, ...props }) => (
                <table
                  {...props}
                  className="!table-fixed border-separate border-spacing-0 text-sm after:absolute after:content-[''] after:w-full after:h-[44px] after:bg-gray-100 after:top-0 after:shadow-[inset_0_-1px_var(--gray-7)]"
                  style={{
                    ...style,
                    width: table.getTotalSize(),
                  }}
                />
              ),

              TableHead: forwardRef((props, ref) => {
                return (
                  <thead
                    {...props}
                    ref={ref}
                    className="bg-gray-100 z-10 sticky top-0"
                  />
                );
              }),
              TableRow: (props) => {
                const rowIndex = props["data-index"];
                const row = rows[rowIndex];

                if (loading) {
                  return (
                    <tr {...props} className="h-12">
                      {new Array(columns.length).fill(null).map((_, index) => (
                        <td
                          key={index}
                          className={twMerge([
                            "p-3 shadow-[inset_0_-1px_var(--gray-7)] border-l border-[var(--gray-7)]",
                            index === 0 && "border-l-0",
                            rowIndex === rows.length - 1 && "shadow-none",
                          ])}
                        >
                          <Skeleton />
                        </td>
                      ))}
                    </tr>
                  );
                }

                return (
                  <tr {...props}>
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={twMerge([
                          "bg-white relative p-3 shadow-[inset_0_-1px_var(--gray-7)] border-l border-[var(--gray-7)]",
                          index === 0 && "border-l-0",
                          rowIndex === rows.length - 1 && "shadow-none",
                          !loading && rowClassName?.(row.original),
                        ])}
                        {...{
                          style: {
                            width:
                              columnSizing[cell.column.id] ||
                              cell.column.getSize(),
                          },
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                );
              },
            }}
            fixedHeaderContent={() => {
              return table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <Th
                      key={header.id}
                      header={header}
                      index={index}
                      columnSizing={columnSizing}
                      updateColumnSize={updateColumnSize}
                      getTotalSize={table.getTotalSize}
                    />
                  ))}
                </tr>
              ));
            }}
          />
        )}

        {data.length === 0 && isSuccess && !loading && (
          <Flex
            justify="center"
            align="center"
            direction="column"
            className="absolute h-[calc(100%-100px)] w-full top-12 left-0 bg-white z-10"
          >
            <ExclamationTriangleIcon
              width={64}
              height={64}
              color="var(--orange-11)"
              className="mb-2"
            />
            <Text as="p" size="5" weight="bold" align="center" mb="1">
              {emptyTitle || "No data available"}
            </Text>
            <Text as="p" size="3">
              {emptyDescription}
            </Text>
          </Flex>
        )}
      </ScrollArea>
    </div>
  );
}

export function Th({
  header,
  index,
  columnSizing,
  updateColumnSize,
  getTotalSize,
}: {
  header: Header<any, unknown>;
  index: number;
  columnSizing: Record<string, number>;
  getTotalSize: () => number;
  updateColumnSize: (headerId: string, newWidth: number) => void;
}) {
  const columnCoords = useRef({ x: 0, width: 0 });
  const ref = useRef<HTMLTableCellElement | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    columnCoords.current.x = e.clientX;

    const styles = window.getComputedStyle(ref.current!);
    columnCoords.current.width = parseInt(styles.width, 10);
    e.currentTarget.classList.add("isResizing");
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      const dx = Math.floor(e.clientX - columnCoords.current.x);
      const table = document.querySelector("table") as HTMLTableElement;

      const width = columnCoords.current.width;
      ref.current!.style.width = width + dx + "px";
      table.style.width = getTotalSize() + dx + "px";
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      columnCoords.current.width = 0;
      columnCoords.current.x = 0;

      e.currentTarget.classList.remove("isResizing");
      updateColumnSize(header.id, parseInt(ref.current!.style.width));
    }
  };

  return (
    <th
      ref={ref}
      className={twMerge([
        "relative p-3 text-left border-l border-[var(--gray-7)] shadow-[inset_0_-1px_var(--gray-7)]",
        index === 0 && "border-l-0",
      ])}
      key={header.id}
      {...{
        colSpan: header.colSpan,
        style: {
          minWidth: columnSizing[header.id] || header.getSize(),
          width: columnSizing[header.id] || header.getSize(),
        },
      }}
    >
      <span className="line-clamp-1">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}

        {header.column.getCanSort() && (
          <Button
            size="1"
            variant="ghost"
            className="p-1 absolute right-3 top-1/2 -translate-y-2"
            color="gray"
            title={
              header.column.getCanSort()
                ? header.column.getNextSortingOrder() === "asc"
                  ? "Sort ascending"
                  : header.column.getNextSortingOrder() === "desc"
                    ? "Sort descending"
                    : "Clear sort"
                : undefined
            }
            onClick={header.column.getToggleSortingHandler()}
          >
            {header.column.getIsSorted() ? (
              <>
                {header.column.getIsSorted() === "asc" ? (
                  <ArrowUpIcon />
                ) : (
                  <ArrowDownIcon />
                )}
              </>
            ) : (
              <CaretSortIcon />
            )}
          </Button>
        )}
      </span>
      {!["select"].includes(header.id) && (
        <div
          className="resizer w-1"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        />
      )}
    </th>
  );
}

export function VisibleColumns({
  table,
  visibleColumnNames,
}: {
  table: ReactTable<any>;
  visibleColumnNames?: Record<string, string>;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger>
        <Button variant="outline" color="gray">
          <LayoutIcon />
        </Button>
      </Popover.Trigger>
      <Popover.Content style={{ width: 220 }}>
        <Text size="3" weight="bold" mb="2" as="p">
          Columns
        </Text>
        {table
          .getAllLeafColumns()
          .filter((column) => !["select"].includes(column.id))
          .map((column) => {
            return (
              <Box py="1" key={column.id}>
                <Text as="label" className="select-none" size="2">
                  <Flex gap="2">
                    <Checkbox
                      checked={column.getIsVisible()}
                      onCheckedChange={(checked) => {
                        column.getToggleVisibilityHandler()({
                          target: {
                            checked,
                          },
                        });
                      }}
                    />{" "}
                    {visibleColumnNames?.[column.id]}
                  </Flex>
                </Text>
              </Box>
            );
          })}
      </Popover.Content>
    </Popover.Root>
  );
}
